import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { CheckCircle, Info, TriangleAlert } from 'lucide-react'
import { useTeam } from 'app/frontend/base/config/context/team'
import useGuildBenefits from 'app/frontend/base/hooks/useGuildBenefits'
import { Link } from 'react-router-dom'
import RefreshStatusBlock from './RefreshStatusBlock'
import { Button } from '@/components/ui/button'
import { useState } from 'react'
import { useReactivateGuild } from 'app/frontend/base/config/mutations'
import useRefetchGuildBenefits from 'app/frontend/base/hooks/useRefetchGuildBenefits'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
}

const RefreshStatusDialog = ({ open, setOpen }: Props) => {
  const { team, guild } = useTeam()
  const {
    refresh_factor: { level },
  } = useGuildBenefits()
  const [helpOpen, setHelpOpen] = useState(false)
  const reactivateGuild = useReactivateGuild()
  useRefetchGuildBenefits(open)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className='max-w-xl'
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Refresh status</DialogTitle>
        </DialogHeader>

        {!guild.active ? (
          <>
            <Alert variant='warning'>
              <TriangleAlert size={16} />
              <AlertTitle>Guild inactive</AlertTitle>
              <AlertDescription>
                This guild has been marked as inactive and is no longer being
                refreshed.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => reactivateGuild.mutate(guild)}
                disabled={reactivateGuild.isPending}
              >
                Reactivate guild
              </Button>
              <Button variant='outline' onClick={() => setOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {gameVersionHas('mythic_plus') ? (
              <div className='grid grid-cols-2 gap-4 mb-2'>
                <RefreshStatusBlock type='collections' />
                {/* <RefreshStatusBlock type='wcl' /> */}
                <RefreshStatusBlock type='raiderio' />
              </div>
            ) : (
              <div className='mb-2'>
                <RefreshStatusBlock type='collections' horizontal />
              </div>
            )}

            <Alert variant={level === 1 ? 'info' : 'success'}>
              {level === 1 ? <Info size={16} /> : <CheckCircle size={16} />}
              <AlertTitle>Refresh frequency</AlertTitle>
              <AlertDescription>
                Your data is refreshing at <strong>{level}</strong> time
                {level === 1 ? '' : 's'} the normal rate. You can apply{' '}
                <Link
                  to={`${team.path}/premium`}
                  className='text-primary hover:text-primary/90'
                  onClick={() => setOpen(false)}
                >
                  boosts
                </Link>{' '}
                to this guild to increase
                {level === 1 ? ' the frequency' : ' it further'}.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant='outline' onClick={() => setHelpOpen(true)}>
                Help
              </Button>
              <Button variant='outline' onClick={() => setOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Refresh status</DialogTitle>
          </DialogHeader>

          <p>
            Here you see the refresh status of the different kinds of data shown
            in your spreadsheet. The <em>last refreshed</em> time visible in
            your spreadsheet will correspond to the last time the Blizzard data
            was refreshed. When for example Warcraft Logs data has refreshed, it
            will not be visible in the spreadsheet until the next time the
            Blizzard data refreshes.{' '}
            <strong>
              The data on this page will update automatically every 30 seconds
            </strong>
            , you don&apos;t have to refresh the page.
          </p>

          <p>
            Keep in mind that pressing the refresh buttons in your spreadsheet
            does not have any effect on this page, it will only retrieve the
            latest data from the wowaudit server (your data won&apos;t refresh
            any faster).
          </p>

          <p>
            There&apos;s a limit to the amount of API requests wowaudit can
            make. Every team&apos;s data will be refreshed on a schedule, but if
            you would like the data to be refreshed more frequently you can
            apply{' '}
            <Link
              to={`${team.path}/premium`}
              className='text-primary hover:text-primary/90'
              onClick={() => {
                setHelpOpen(false)
                setOpen(false)
              }}
            >
              boosts
            </Link>{' '}
            to this guild.
          </p>

          <DialogFooter>
            <Button variant='outline' onClick={() => setHelpOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}

export default RefreshStatusDialog
